// These advisory warnings mirror the backend's store-page lint without blocking a save.
import type { LintWarning, PlayBeat, StorePageContent } from "./types";
/** Case-insensitive `policy`/`policies`, skipping anything inside `` `...` `` —
 * a code-block-looking span, where the word is naming an API, not a player. */
function sayPlayerNotPolicy(text: string): boolean {
  const outsideCode = text.replace(/`[^`]*`/g, "");
  return /\b(polic(?:y|ies))\b/i.test(outsideCode);
}
function noTypicalEpisode(text: string): boolean {
  return /typical episode/i.test(text);
}
/** ALL CAPS or Title Case Throughout fails; Sentence case passes. A first
 * word that isn't `Capitalized, then lowercase` fails outright (catches ALL
 * CAPS); otherwise every word after the first being independently
 * capitalized fails too (catches Title Case Throughout). Regex-simple, not
 * NLP — a short proper-noun-only slot ("New York") can false-positive; the
 * warning is advisory, not a hard block. */
function isSentenceCase(text: string): boolean {
  const words = text.match(/[A-Za-z]+/g);
  const [firstWord, ...rest] = words ?? [];

  if (firstWord === undefined) return true;

  const firstOk =
    firstWord.charAt(0) === firstWord.charAt(0).toUpperCase() &&
    firstWord.charAt(0) !== firstWord.charAt(0).toLowerCase();
  const restOfFirstOk = firstWord.slice(1) === firstWord.slice(1).toLowerCase();

  if (!firstOk || !restOfFirstOk) return false;

  if (rest.length === 0) return true;

  const allSubsequentCapitalized = rest.every(
    (word) =>
      word.charAt(0) === word.charAt(0).toUpperCase() &&
      word.charAt(0) !== word.charAt(0).toLowerCase() &&
      word.slice(1) === word.slice(1).toLowerCase(),
  );
  return !allSubsequentCapitalized;
}
function noBoldMarkdown(text: string): boolean {
  return /\*\*[^*]+\*\*/.test(text) || /__[^_]+__/.test(text);
}
/** Every check against one field's text, appended to `warnings`. */
function lintField({
  field,
  text,
  warnings,
}: {
  field: string;
  text: string;
  warnings: LintWarning[];
}): void {
  if (!text) return;

  if (sayPlayerNotPolicy(text)) {
    warnings.push({
      field,
      rule: "say_player_not_policy",
      message: "say 'player', not 'policy' — house style",
    });
  }

  if (noTypicalEpisode(text)) {
    warnings.push({
      field,
      rule: "no_typical_episode",
      message: "never 'typical episode' — house style",
    });
  }

  if (!isSentenceCase(text)) {
    warnings.push({
      field,
      rule: "sentence_case",
      message: "sentence case, not Title Case or ALL CAPS",
    });
  }

  if (noBoldMarkdown(text)) {
    warnings.push({
      field,
      rule: "no_bold_markdown",
      message: "no bold — plain text only",
    });
  }
}
/** Every string slot: `blurb`, `cta_label`, `closing_line`, each beat's
 * `title`/`body`, each link's `label` (CONTRACT.md §4, links added
 * 2026-09-25). Media alt text and link URLs are not linted — alt text is not
 * one of the "house style" prose slots the rules were written for, and a URL
 * has its own hard validation (`limits.ts`), not house-style prose rules. */
export function lintStorePageContent(content: StorePageContent): LintWarning[] {
  const warnings: LintWarning[] = [];

  if (content.blurb)
    lintField({
      field: "blurb",
      text: content.blurb,
      warnings,
    });

  if (content.cta_label)
    lintField({
      field: "cta_label",
      text: content.cta_label,
      warnings,
    });

  if (content.closing_line)
    lintField({
      field: "closing_line",
      text: content.closing_line,
      warnings,
    });

  (content.play_beats ?? []).forEach((beat: PlayBeat, index: number) => {
    lintField({
      field: `play_beats[${index}].title`,
      text: beat.title,
      warnings,
    });
    lintField({
      field: `play_beats[${index}].body`,
      text: beat.body,
      warnings,
    });
  });
  (content.links ?? []).forEach((link, index: number) => {
    lintField({
      field: `links[${index}].label`,
      text: link.label,
      warnings,
    });
  });
  return warnings;
}
